import moment from "moment/moment";
import { enqueueSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";
import { CoralogixRum } from "@coralogix/browser";

import {
  appUtilityKeys,
  contactStakeIfErrorPersistsMessage,
  ErrorToastVariant,
  InfoToastVariant,
  neglectEndpointsForUnauthorisedErrors,
  SuccessToastVariant,
  WarningToastVariant,
  weekdayNames,
} from "./constants";

import { normalizeSsoPrefillFlatPayload } from "./sso-prefill-normalize";

import {
  CheckCircle,
  Cancel,
  Warning,
  Blitz,
  BankingOutline,
  Link,
  HourGlass,
  Clock,
  Recurring,
  Close,
  CheckCircleOutline,
  Home,
  DealsCircle,
  CashTransfer,
  NotAllowed,
  Retry,
  Share,
  UpcomingSchedule,
} from "../Assets/Icons";

export const getNavItems = ({ hideDealsRoutes, hideCashRoutes }) => [
  { Icon: Home, navItemName: "Home", navRoute: "/home" },
  ...(hideDealsRoutes ? [] : [{ Icon: DealsCircle, navItemName: "Deals", navRoute: "/deals" }]),
  ...(hideCashRoutes ? [] : [{ Icon: CashTransfer, navItemName: "Cash", navRoute: "/cash" }]),
];

export const getToastVariantIcon = (type) => {
  switch (type) {
    case "success":
      return CheckCircle;
    case "error":
      return Cancel;
    case "warning":
      return Warning;
    case "info":
      return Warning;
    default:
      return () => <></>;
  }
};

export const getPayrollStatusIcon = (type) => {
  switch (type) {
    case "success":
      return CheckCircle;
    case "pending":
      return HourGlass;
    case "disconnected":
      return Link;
    default:
      return () => <></>;
  }
};

export const getRentPaymentStatusProps = (type) => {
  switch (type) {
    case "Failed":
      return { StatusIcon: Close, statusDisplayName: "Failed", statusType: "negative" };
    case "Rejected":
      return { StatusIcon: Retry, statusDisplayName: "Returned", statusType: "negative" };
    case "Pending":
      return { StatusIcon: Clock, statusDisplayName: "Pending", statusType: "positive" };
    case "InProgress":
      return { StatusIcon: Share, statusDisplayName: "Transfer Initiated", statusType: "positive" };
    case "Success":
      return { StatusIcon: CheckCircleOutline, statusDisplayName: "Success", statusType: "positive" };
    case "Canceled":
      return { StatusIcon: NotAllowed, statusDisplayName: "Canceled", statusType: "negative" };
    case "Upcoming":
      return { StatusIcon: UpcomingSchedule, statusDisplayName: "Upcoming", statusType: "positive" };
    case "Active":
      return { StatusIcon: Recurring, statusDisplayName: "Ongoing", statusType: "positive" };
    default:
      return () => <></>;
  }
};

export const getRentPaymentStatusItems = ({ isAutopay, paymentStatus } = {}) => {
  const paymentStatusFlags = {
    isScheduled: false,
    isTransferInitiated: false,
    isOngoing: false,
    isCancelled: false,
  };
  let StatusIcon = null;
  let statusBadgeName = "";
  let statusDisplayText = "";

  if (isAutopay && paymentStatus !== "Transfer Initiated") {
    paymentStatusFlags.isOngoing = true;
    StatusIcon = Recurring;
    statusBadgeName = "blue";
    statusDisplayText = "Autopay";
  } else {
    statusDisplayText = paymentStatus;
    switch (paymentStatus) {
      case "Scheduled":
        paymentStatusFlags.isScheduled = true;
        StatusIcon = Clock;
        statusBadgeName = "green";
        break;
      case "Transfer Initiated":
        paymentStatusFlags.isTransferInitiated = true;
        StatusIcon = CheckCircleOutline;
        statusBadgeName = "green";
        break;
      case "Cancelled":
        paymentStatusFlags.isCancelled = true;
        StatusIcon = Close;
        statusBadgeName = "red";
        break;
      default:
        break;
    }
  }

  return { paymentStatusFlags, statusBadgeName, statusDisplayText, StatusIcon };
};

export const isActiveModule = (modulePath, currentPath) => {
  return currentPath.startsWith(modulePath);
};

export const isOnboardingModule = (pathname) => {
  return ["/login", "/welcome", "/unclaimed", "/setup-account"].includes(pathname) || pathname.startsWith("/welcome/");
};

//Local Storage CRUD Helper

export const LocalStorage = {
  write: (key, val) => {
    if (val && typeof val === "object") localStorage.setItem(key, JSON.stringify(val));
    else localStorage.setItem(key, val);
  },
  read: (key) => {
    const str = localStorage.getItem(key);
    if (str === null) {
      return null;
    }

    // Check if the value is a boolean string ("true" or "false")
    if (str === "true" || str === "false") {
      return str === "true";
    }

    // Check if the value is a JSON object or array
    if (["{", "["].includes(str.charAt(0))) {
      try {
        return JSON.parse(str);
      } catch (e) {
        console.error("Failed to parse JSON:", e);
        return null;
      }
    }

    // If it's neither a boolean nor a JSON object, return the string as is
    return str;
  },
  remove: (key) => {
    localStorage.removeItem(key);
  },
  clear: () => localStorage.clear(),
};

//Toast Dispatch helpers

export const dispatchSuccessToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: SuccessToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

export const dispatchErrorToast = (message, options = {}) =>
  enqueueSnackbar(message ?? "Oops! Something went wrong!", {
    variant: ErrorToastVariant,
    key: uuidv4(),
    preventDuplicate: true,
    ...options,
  });

export const dispatchAPIErrorToast = ({ message, ...options } = {}) =>
  dispatchErrorToast(!message ? contactStakeIfErrorPersistsMessage : message, options);

export const dispatchWarningToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: WarningToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

export const dispatchInfoToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: InfoToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

//Data Tranformation helpers

export const replaceNonNumericCharacters = (number) => number?.replace(/[^\d]/gi, "");

export const transformUSNumberToCountryCodeFormat = (number) => `+1${replaceNonNumericCharacters(number)}`;

export const removeCountryCodeFromPhoneNumber = (number) => number.replace("+1", "");

export const transformCountryCodeFormatToUSNumber = (number) => {
  var cleaned = removeCountryCodeFromPhoneNumber(number).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return "";
};

export const getNumberFromCurrentAmount = (a) => Number(a.replace(/[^0-9.-]+/g, ""));

export const getMaskedSSN = (ssn) => {
  const nonFormattedSSN = replaceNonNumericCharacters(ssn);
  const len = nonFormattedSSN.length;
  const stars =
    len > 0 ? (len > 1 ? (len > 2 ? (len > 3 ? (len > 4 ? "••• •• " : "••• •") : "••• ") : "••") : "•") : "";
  return stars + nonFormattedSSN.substring(5);
};

export const getFormattedSSN = (ssn) => {
  const numericSSN = replaceNonNumericCharacters(ssn);
  return `${numericSSN.substring(0, 3)} ${numericSSN.substring(3, 5)} ${numericSSN.substring(5, 9)}`;
};

/*eslint no-control-regex: "off"*/
export const removeControlCharacters = (str) => str.replace(/[^\x00-\x7F]/g, "");

//API Request config builder

export const getAPIConfig = (builder = []) => {
  const headers = new Headers();

  if (builder.includes("authKey")) {
    const authKey = LocalStorage.read(appUtilityKeys.authKey);
    headers.append("auth", authKey);
  }

  if (builder.includes("xAccessToken")) {
    const authKey = LocalStorage.read(appUtilityKeys.authKey);
    headers.append("x-access-token", authKey);
  }

  if (builder.includes("transferToken")) {
    const transferToken = LocalStorage.read(appUtilityKeys.transferToken);
    headers.append("auth", transferToken);
  }

  const user = LocalStorage.read(appUtilityKeys.loggedInUserData);

  if (builder.includes("userId") && !!user) {
    headers.append("userId", user._id ?? "");
  }

  if (builder.includes("user") && !!user) {
    headers.append("user", user._id ?? "");
  }

  if (builder.includes("userEmail") && !!user) {
    headers.append("user-email", user.email ?? "");
  }

  if (builder.includes("astraToken")) {
    const astraToken = LocalStorage.read(appUtilityKeys.astraToken) ?? "";
    headers.append("Authorization", astraToken);
  }

  if (builder.includes("formData")) {
    headers.append("Content-Type", "multipart/form-data");
  }

  if (builder.includes("stakePay3AuthToken")) {
    const authToken = LocalStorage.read(appUtilityKeys.stakePay3AuthToken);
    headers.append("Token", authToken);
  }

  return {
    headers: Object.fromEntries(headers.entries()),
  };
};

//Object Attribute Inject helper

export const injectAttributes = (obj, builder = []) => {
  const user = LocalStorage.read(appUtilityKeys.loggedInUserData);

  if (builder.includes("userId") && user) {
    obj.userId = user._id;
  }

  return obj;
};

//API Interceptors
export const CoreAPIRequestInterceptor = (req) => {
  req.headers["dealsAPIKey"] = process.env.REACT_APP_CORE_API_KEY;
  return req;
};

export const APIErrorInterceptor = (err) => {
  if (!neglectEndpointsForUnauthorisedErrors.find((endpoint) => err?.request?.responseURL.endsWith(endpoint))) {
    if ([401, 403].find((e) => e === err?.response?.status)) {
      onSignOut();
      return;
    }
  }
  throw err;
};

//Formatting helpers
export const capitaliseString = (str) => {
  return (str?.charAt(0) ?? "").toUpperCase() + (str?.slice(1) ?? "").toLowerCase();
};

export const formatNumber = (number, decimalPoints = 2) => {
  if (!isNaN(number)) {
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: decimalPoints,
      maximumFractionDigits: decimalPoints,
    }).format(number);
  }
  return null;
};

export const formatCurrency = (number, decimalPoints = 2) => {
  if (!isNaN(number)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimalPoints,
      maximumFractionDigits: decimalPoints,
    }).format(number);
  }
  return null;
};

export const formatNumberWithCentsToUSD = (amount) => {
  // Divide by 100 to move the last two digits into cents
  return formatCurrency(amount / 100);
};

export const getUnclaimedCashBackAmount = (unclaimedTransactionsResponse) => {
  const unclaimedData = unclaimedTransactionsResponse?.data?.data;
  const unclaimedTotal =
    unclaimedData?.unclaimed_transactions?.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    ) ?? 0;
  return (
    unclaimedData?.total_cash_back_unlocked ??
    formatCurrency(unclaimedTotal) ??
    "$0"
  );
};

export const getUnclaimedCashBackAmountNumeric = (unclaimedTransactionsResponse) => {
  const unclaimedData = unclaimedTransactionsResponse?.data?.data;
  const unclaimedTotal =
    unclaimedData?.unclaimed_transactions?.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    ) ?? 0;
  if (unclaimedTotal > 0) return unclaimedTotal;
  return getNumberFromCurrentAmount(getUnclaimedCashBackAmount(unclaimedTransactionsResponse));
};

export const buildCheckRequestAddress = ({ address1, address2, city, state, zipCode }) => ({
  address_line1: [address1, address2].filter(Boolean).join(", "),
  city,
  state,
  zip: zipCode,
  country: "US",
});

export const formatDate = (dateString, options = {}) => {
  if (!!dateString && !isNaN(new Date(dateString))) {
    return new Intl.DateTimeFormat("en-US", options).format(new Date(dateString));
  }
  return null;
};

export const getISOFormatDate = (date) => date?.toISOString().split("T")[0];

export const appendISOTime = (date) => `${date}T00:00:00`;

export const normalizeDateToMidnight = (date = new Date()) => {
  const normalizeDate = new Date(new Date(date).setHours(0, 0, 0, 0));
  return normalizeDate;
};

/** `/mobile/deals/` response shape: `data.data.currentDeal.account_migration.status` (e.g. `migration_not_yet_started`). */
export const getBankAccountMigrationStatusFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.account_migration?.status;
};

export const getBankAccountMigrationEndDateFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.account_migration?.migration_end_date;
};

export const getBankAccountMigrationCanSkipFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.account_migration?.can_skip;
};

export const shouldShowBankAccountMigrationSkipFromDealsResponse = (dealsResponse) => {
  if (!getBankAccountMigrationCanSkipFromDealsResponse(dealsResponse)) {
    return false;
  }

  const migrationEndDate = getBankAccountMigrationEndDateFromDealsResponse(dealsResponse);
  if (!migrationEndDate) {
    return false;
  }

  const today = normalizeDateToMidnight();
  const endDate = normalizeDateToMidnight(migrationEndDate);
  const daysUntilEnd = Math.floor((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  return daysUntilEnd > 7;
};

export const isBankAccountMigrationRequiredByDealsResponse = (dealsResponse) => {
  const status = getBankAccountMigrationStatusFromDealsResponse(dealsResponse);
  return typeof status === "string" && status.length > 0 && status === "migration_not_yet_started";
};

export const isBankAccountMigrationNotRequiredByDealsResponse = (dealsResponse) => {
  return getBankAccountMigrationStatusFromDealsResponse(dealsResponse) === "migration_not_required";
};

export const isUmfUserFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.is_umf_user === false;
};

export const isBattlecardUserFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.is_battlecard_user === true;
};

export const isInstantReleaseUserFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.is_instant_release === true;
};

export const isActiveUmfUserFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.is_umf_user === true;
};

export const getBattlecardSpendTrackerFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.battlecard_spend_tracker ?? null;
};

export const shouldShowMoveInBonusTrackerFromDealsResponse = (dealsResponse) => {
  if (dealsResponse?.data?.message !== "SUCCESS") return false;
  if (!isBattlecardUserFromDealsResponse(dealsResponse)) return false;

  const plan = dealsResponse?.data?.data?.currentDeal?.move_in_cash_plan;
  if (plan !== "basic" && plan !== "unlimited") return false;

  const tracker = getBattlecardSpendTrackerFromDealsResponse(dealsResponse);
  if (!tracker) return false;
  if (tracker.show_unlocked_screen === false) return false;

  return true;
};

export const hasUnclaimedTransactions = (unclaimedTransactionsResponse) => {
  return (unclaimedTransactionsResponse?.data?.data?.unclaimed_transactions?.length ?? 0) > 0;
};

export const shouldShowUnclaimedPage = (dealsResponse, unclaimedTransactionsResponse) => {
  return (
    !isBattlecardUserFromDealsResponse(dealsResponse) &&
    !isInstantReleaseUserFromDealsResponse(dealsResponse) &&
    !isStakeUtilitiesUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse)
  );
};

/**
 * Maps `/api/sso/prefill` inner `data` (axios: `response.data.data`) to the shape previously returned by
 * stake-saver prefill. Example API body:
 * `{ success, status_code, data: { firstname, lastname, email, "mobile number", mailingstreet,
 * mailingcity, mailingstate, mailingpostalcode, "ssn number", citizenship, employer_name, industry,
 * occupation, expected_monthly_deposit, expected_monthly_withdrawal, ... }, msg }`
 */
export const mapSsoPrefillToStakeSaverFormPrefillShape = (ssoPayload) => {
  const empty = {
    is_out_of_network_user: false,
    prefill_info: {},
    descriptions: { signature_acknowledgement: "", offer_disclaimer: "" },
  };

  if (!ssoPayload || typeof ssoPayload !== "object") {
    return empty;
  }

  const p = normalizeSsoPrefillFlatPayload(ssoPayload);
  const val = (keys) => {
    for (const k of keys) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      const v = p[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  };

  const prefill_info = {
    first_name: val(["first_name", "firstname"]),
    last_name: val(["last_name", "lastname"]),
    phone: val(["phone", "mobile number", "mobile_number"]),
    email: val(["email"]),
    address1: val(["address1", "mailingstreet"]),
    address2: val(["address2", "apt_number"]),
    city: val(["city", "mailingcity"]),
    state: val(["state", "mailingstate"]),
    zipcode: val(["zipcode", "mailingpostalcode"]),
    dob: val(["dob", "birthdate"]),
    ssn: val(["ssn", "ssn number"]),
    occupation: val(["occupation"]),
    citizenship: val(["citizenship"]),
    employer_name: val(["employer_name", "employerName"]),
    industry: val(["industry"]),
    rent_amount: val(["rent_amount", "rentAmount"]),
    lease_start_date: val(["lease_start_date", "leaseStartDate"]),
    lease_end_date: val(["lease_end_date", "leaseEndDate"]),
    expected_monthly_deposit: val(["expected_monthly_deposit", "expectedMonthlyDeposit"]),
    expected_monthly_withdrawal: val(["expected_monthly_withdrawal", "expectedMonthlyWithdrawal"]),
  };

  const is_out_of_network_user = Boolean(
    p.is_out_of_network_user === true ||
      p.is_out_of_network_user === "true" ||
      p.isOutOfNetworkUser === true,
  );

  const rawDescriptions = p.descriptions;
  const descriptions =
    rawDescriptions && typeof rawDescriptions === "object"
      ? {
          signature_acknowledgement: rawDescriptions.signature_acknowledgement ?? "",
          offer_disclaimer: rawDescriptions.offer_disclaimer ?? "",
        }
      : {
          signature_acknowledgement: p.signature_acknowledgement ?? "",
          offer_disclaimer: p.offer_disclaimer ?? "",
        };

  return {
    is_out_of_network_user,
    prefill_info,
    descriptions,
  };
};

/** `/mobile/stake_debit/` response shape: `data.data.migrating_bank_status` (`VerificationPending`, `VerificationSubmitted`, `VerificationRequired`, `VerificationRejected`). */
export const getMigratingBankStatusFromStakeDebitResponse = (stakeDebitResponse) => {
  return stakeDebitResponse?.data?.data?.migrating_bank_status;
};

export const shouldShowMigrationAcknowledgementFromStakeDebitResponse = (stakeDebitResponse) => {
  return !!stakeDebitResponse?.data?.data?.show_migration_acknowledgement;
};

export const getStakeDebitApplicationStatus = (stakeDebit) =>
  stakeDebit?.data?.data?.status ?? stakeDebit?.data?.status;

export const getSyncteraSessionTokenFromStakeDebitResponse = (stakeDebitResponse) => {
  const inner = stakeDebitResponse?.data?.data;
  const outer = stakeDebitResponse?.data;
  return (
    inner?.synctera_session_token ??
    inner?.session_token ??
    outer?.synctera_session_token ??
    outer?.session_token
  );
};

export const getVerifyPersonSyncteraIdPayloadFromStakeDebitResponse = (stakeDebitResponse) => {
  const syncteraPersonId = stakeDebitResponse?.data?.data?.synctera_person_id;

  if (syncteraPersonId) {
    return { synctera_person_id: syncteraPersonId };
  }

  return { person_id: stakeDebitResponse?.data?.data?.unit_customer_id };
};

export const BANK_MIGRATION_TRACKER_STATUS = {
  APPLICATION_PENDING: "application_pending",
  UNDER_REVIEW: "under_review",
  VERIFICATION_REQUIRED: "verification_required",
  VERIFICATION_REJECTED: "verification_rejected",
};

export const getBankMigrationTrackerVariantFromMigratingBankStatus = (status) => {
  if (typeof status !== "string" || !status.length) {
    return null;
  }

  const normalized = status.trim().replace(/[\s-_]/g, "").toLowerCase();

  if (normalized === "verificationpending") {
    return BANK_MIGRATION_TRACKER_STATUS.APPLICATION_PENDING;
  }

  if (normalized === "verificationunderreview") {
    return BANK_MIGRATION_TRACKER_STATUS.UNDER_REVIEW;
  }

  if (normalized === "verificationrequired") {
    return BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REQUIRED;
  }

  if (normalized === "verificationrejected") {
    return BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REJECTED;
  }

  return null;
};

export const isBankMigrationVerificationRequiredFromStakeDebitResponse = (stakeDebitResponse) => {
  return (
    getBankMigrationTrackerVariantFromMigratingBankStatus(
      getMigratingBankStatusFromStakeDebitResponse(stakeDebitResponse),
    ) === BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REQUIRED
  );
};

/**
 * True when the user has already submitted bank migration (Synctera/migrate flow). Deals
 * `account_migration.status` can lag as `migration_not_yet_started`; without this guard,
 * {@link RedirectContext} would send them back to `/bank-account-migration` after navigating home.
 */
export const hasBankMigrationApplicationStartedFromStakeDebitResponse = (stakeDebitResponse) => {
  const migrating = getMigratingBankStatusFromStakeDebitResponse(stakeDebitResponse);
  if (typeof migrating === "string" && migrating.trim().length > 0) {
    return true;
  }
  return isSVDVerificationRequired(stakeDebitResponse);
};

export const getBankMigrationNextStepsCardFromTrackerVariant = (variant) => {
  const config = {
    [BANK_MIGRATION_TRACKER_STATUS.APPLICATION_PENDING]: {
      title: "Required bank migration",
      subtitle: "Your account is being set up. Banking features will resume shortly.",
      theme: "migration-pending",
    },
    [BANK_MIGRATION_TRACKER_STATUS.UNDER_REVIEW]: {
      title: "Required bank migration",
      subtitle: "We're reviewing your info. Banking features will be back once approved.",
      theme: "migration-pending",
    },
    [BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REQUIRED]: {
      title: "Required Bank Migration",
      subtitle: "Additional verification needed. Tap to continue.",
      theme: "migration-required",
    },
    [BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REJECTED]: {
      title: "Required Bank Migration",
      subtitle: "We couldn't finish your migration. Tap here to contact support.",
      theme: "migration-required",
      openSupport: true,
    },
  };

  return config[variant] ?? null;
};

//SVD Status Checkers

export const isSVDApproved = (stakeDebit) => {
  const status = getStakeDebitApplicationStatus(stakeDebit);
  return (
    !!stakeDebit &&
    (status === "ApplicationApproved" ||
      status === "DepositAccountCreated" ||
      status === "DebitCardRequested" ||
      status === "DebitCardActivated")
  );
};

export const isSVDPendingVerification = (stakeDebit) => {
  const status = getStakeDebitApplicationStatus(stakeDebit);
  return (
    !!stakeDebit &&
    (status === "VerificationRequired" ||
      status === "ApplicationPendingAddressVerification" ||
      status === "ApplicationPendingIDVerification" ||
      status === "ApplicationPendingSSNVerification")
  );
};

export const isSVDVerificationRequired = (stakeDebit) => {
  return !!stakeDebit && getStakeDebitApplicationStatus(stakeDebit) === "VerificationRequired";
};

export const isSVDPending = (stakeDebit) => {
  return !!stakeDebit && ["ApplicationPending", "VerificationPending"].includes(stakeDebit?.data?.status);
};

export const isSVDVerificationPendingStatus = (stakeDebit) => {
  return !!stakeDebit && getStakeDebitApplicationStatus(stakeDebit) === "VerificationPending";
};

export const isSVDVerificationRejected = (stakeDebit) => {
  return !!stakeDebit && getStakeDebitApplicationStatus(stakeDebit) === "VerificationRejected";
};

export const BATTLECARD_LEGACY_ONLY_STATE = { hideBasicAndUnlimited: true };

export const isSVDDepositAccountCreated = (stakeDebit) => {
  return !!stakeDebit && getStakeDebitApplicationStatus(stakeDebit) === "DepositAccountCreated";
};

export const isSVDApplicationApproved = (stakeDebit) => {
  return !!stakeDebit && getStakeDebitApplicationStatus(stakeDebit) === "ApplicationApproved";
};

export const isSVDReview = (stakeDebit) => {
  return !!stakeDebit && ["VerificationSubmitted", "VerificationUnderReview"].includes(stakeDebit?.data?.status);
};

export const shouldRouteHomeAfterDocvVerification = (stakeDebit) => {
  return isSVDReview(stakeDebit);
};

export const hasSVDVirtualCard = (stakeDebit) => {
  return (
    !!stakeDebit &&
    !!stakeDebit?.data?.data?.virtual_card_id &&
    !!stakeDebit?.data?.data?.virtual_card_expiration &&
    !!stakeDebit?.data?.data?.virtual_last_4_digits
  );
};

export const hasSVDPhysicalCard = (stakeDebit) => {
  return !!stakeDebit && ["DebitCardRequested", "DebitCardActivated"].includes(stakeDebit?.data?.status);
};

export const isSVDCardRequested = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "DebitCardRequested";
};

export const isSVDDebitCardActivated = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "DebitCardActivated";
};

export const isSVDEligible = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "Eligible";
};

export const shouldShowBattleCardsPage = (dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) => {
  return (
    isBattlecardUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isSVDEligible(stakeDebitResponse?.data)
  );
};

export const shouldShowLegacyOnlyBattlecardPage = (
  dealsResponse,
  unclaimedTransactionsResponse,
  stakeDebitResponse
) => {
  return (
    isBattlecardUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isSVDVerificationRejected(stakeDebitResponse?.data)
  );
};

export const shouldShowInstantReleasePage = (dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) => {
  return (
    isInstantReleaseUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isSVDEligible(stakeDebitResponse?.data)
  );
};

export const shouldShowLegacyOnlyInstantReleasePage = (
  dealsResponse,
  unclaimedTransactionsResponse,
  stakeDebitResponse
) => {
  return (
    isInstantReleaseUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isSVDVerificationRejected(stakeDebitResponse?.data)
  );
};

/** Statuses between applying for Stake Checking and the Synctera account being created. */
const INSTANT_RELEASE_IN_PROGRESS_STATUSES = [
  "ApplicationPending",
  "ApplicationPendingIDVerification",
  "ApplicationPendingAddressVerification",
  "ApplicationPendingSSNVerification",
  "ApplicationApproved",
  "VerificationRequired",
  "VerificationPending",
  "VerificationSubmitted",
  "VerificationUnderReview",
];

export const isInstantReleaseApplicationInProgress = (stakeDebitResponse) =>
  INSTANT_RELEASE_IN_PROGRESS_STATUSES.includes(getStakeDebitApplicationStatus(stakeDebitResponse?.data));

/** An instant-release member whose application is still being processed (until the webhook creates the account). */
export const shouldShowInstantReleaseResumePage = (dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) => {
  return (
    isInstantReleaseUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isInstantReleaseApplicationInProgress(stakeDebitResponse)
  );
};

/** The stake_utilities offer config. The backend sends `is_stake_utilities`; the raw offer_config is accepted too. */
export const isStakeUtilitiesUserFromDealsResponse = (dealsResponse) => {
  const currentDeal = dealsResponse?.data?.data?.currentDeal;
  return (
    currentDeal?.is_stake_utilities === true ||
    String(currentDeal?.offer_config ?? "")
      .trim()
      .toLowerCase() === "stake_utilities"
  );
};

export const shouldShowStakeUtilitiesPage = (dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) => {
  return (
    isStakeUtilitiesUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isSVDEligible(stakeDebitResponse?.data)
  );
};

/** A stake_utilities member whose application is still being processed. */
export const shouldShowStakeUtilitiesResumePage = (dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) => {
  return (
    isStakeUtilitiesUserFromDealsResponse(dealsResponse) &&
    hasUnclaimedTransactions(unclaimedTransactionsResponse) &&
    isInstantReleaseApplicationInProgress(stakeDebitResponse)
  );
};

export const getBattlecardRedirectNavState = (
  dealsResponse,
  unclaimedTransactionsResponse,
  stakeDebitResponse
) => {
  if (shouldShowLegacyOnlyBattlecardPage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse)) {
    return { ...BATTLECARD_LEGACY_ONLY_STATE, ...BATTLECARD_FLOW_NAV_STATE };
  }

  return BATTLECARD_FLOW_NAV_STATE;
};

export const shouldRedirectToBattlecardPage = (
  dealsResponse,
  unclaimedTransactionsResponse,
  stakeDebitResponse
) => {
  return (
    shouldShowBattleCardsPage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) ||
    shouldShowLegacyOnlyBattlecardPage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse)
  );
};

const BATTLECARD_FLOW_ACCESS_KEY = "battlecard_flow_access";

export const isBattlecardRoute = (pathname) => {
  return typeof pathname === "string" && pathname.startsWith("/battlecard");
};

export const isBattlecardEntryRoute = (pathname) => {
  return pathname === "/battlecard";
};

export const BATTLECARD_FLOW_PROTECTED_ROUTES = [
  "/battlecard",
  "/battlecard/legacy-terms",
  "/battlecard/validate-address",
  "/battlecard/check-mailing-address",
  "/battlecard/check-on-the-way",
  "/battlecard/check-not-processed",
  "/battlecard/start-shopping",
  "/battlecard/member-setup",
  "/battlecard/pay-rent",
  "/battlecard/add-funds",
];

/** @deprecated Use isBattlecardFlowProtectedRoute */
export const DIRECT_URL_RESTRICTED_BATTLECARD_ROUTES = BATTLECARD_FLOW_PROTECTED_ROUTES.filter(
  (route) => route !== "/battlecard",
);

export const isBattlecardFlowProtectedRoute = (pathname) => {
  return BATTLECARD_FLOW_PROTECTED_ROUTES.includes(pathname);
};

export const isDirectUrlRestrictedBattlecardRoute = (pathname) => {
  return isBattlecardFlowProtectedRoute(pathname);
};

export const isLegacyBattlecardRoute = (pathname) => {
  return [
    "/battlecard/check-mailing-address",
    "/battlecard/check-on-the-way",
    "/battlecard/check-not-processed",
  ].includes(pathname);
};

export const grantBattlecardFlowAccess = () => {
  sessionStorage.setItem(BATTLECARD_FLOW_ACCESS_KEY, "1");
};

export const hasBattlecardFlowAccess = () => {
  return sessionStorage.getItem(BATTLECARD_FLOW_ACCESS_KEY) === "1";
};

export const clearBattlecardFlowAccess = () => {
  sessionStorage.removeItem(BATTLECARD_FLOW_ACCESS_KEY);
};

export const BATTLECARD_FLOW_NAV_STATE = { battlecardFlow: true };

export const BATTLECARD_PLAN_SELECTION_NAV_STATE = {
  ...BATTLECARD_FLOW_NAV_STATE,
  hideLegacy: true,
};

export const isLegacyMoveInCashPlanFromDealsResponse = (dealsResponse) => {
  return (
    dealsResponse?.data?.message === "SUCCESS" &&
    dealsResponse?.data?.data?.currentDeal?.move_in_cash_plan === "legacy"
  );
};

export const shouldHideLegacyPlanUpgradeOptions = (dealsResponse, stakeDebitResponse) => {
  return (
    isLegacyMoveInCashPlanFromDealsResponse(dealsResponse) &&
    isSVDVerificationRejected(stakeDebitResponse?.data)
  );
};

export const getLegacyCheckStatusFromDealsResponse = (dealsResponse) => {
  return dealsResponse?.data?.data?.currentDeal?.check_status;
};

export const getCheckStatusAmountFromDealsResponse = (dealsResponse) => {
  const amount = getLegacyCheckStatusFromDealsResponse(dealsResponse)?.amount;
  if (amount == null || amount === "") return null;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getCheckIdFromDealsResponse = (dealsResponse) =>
  getLegacyCheckStatusFromDealsResponse(dealsResponse)?.check_id;

export const getBattlecardCashBackAmount = (dealsResponse, unclaimedTransactionsResponse) => {
  if (isLegacyMoveInCashPlanFromDealsResponse(dealsResponse) || isInstantReleaseUserFromDealsResponse(dealsResponse)) {
    const checkAmount = getCheckStatusAmountFromDealsResponse(dealsResponse);
    if (checkAmount != null) {
      return formatCurrency(checkAmount, 0);
    }
  }

  return getUnclaimedCashBackAmount(unclaimedTransactionsResponse);
};

export const getBattlecardCashBackAmountNumeric = (dealsResponse, unclaimedTransactionsResponse) => {
  if (isLegacyMoveInCashPlanFromDealsResponse(dealsResponse) || isInstantReleaseUserFromDealsResponse(dealsResponse)) {
    const checkAmount = getCheckStatusAmountFromDealsResponse(dealsResponse);
    if (checkAmount != null) {
      return checkAmount;
    }
  }

  return getUnclaimedCashBackAmountNumeric(unclaimedTransactionsResponse);
};

export const isFidelEnabledFromDealsResponse = (dealsResponse) => {
  const currentDeal = dealsResponse?.data?.data?.currentDeal;
  if (currentDeal?.is_fidel_enabled != null) {
    return Boolean(currentDeal.is_fidel_enabled);
  }

  return Boolean(dealsResponse?.data?.data?.is_fidel_enabled);
};

const FIDEL_DEBIT_CASHBACK_FEATURE_LABEL = "2x Cash Back on Stake Debit";

export const withoutFidelDebitCashbackFeature = (features, dealsResponse) => {
  if (isFidelEnabledFromDealsResponse(dealsResponse)) {
    return features;
  }

  return features.filter((feature) => feature.label !== FIDEL_DEBIT_CASHBACK_FEATURE_LABEL);
};

export const canChangeLegacyCheckPlanFromDealsResponse = (dealsResponse, stakeDebitResponse) => {
  if (shouldHideLegacyPlanUpgradeOptions(dealsResponse, stakeDebitResponse)) {
    return false;
  }

  return getLegacyCheckStatusFromDealsResponse(dealsResponse)?.status?.toLowerCase() === "property_approved";
};

export const getLegacyCheckStatusValueFromDealsResponse = (dealsResponse) => {
  return getLegacyCheckStatusFromDealsResponse(dealsResponse)?.status?.toLowerCase() ?? null;
};

export const isLegacyCheckDeliveredFromDealsResponse = (dealsResponse) => {
  return (
    isLegacyMoveInCashPlanFromDealsResponse(dealsResponse) &&
    getLegacyCheckStatusValueFromDealsResponse(dealsResponse) === "delivered"
  );
};

export const isLegacyCheckPendingFromDealsResponse = (dealsResponse) => {
  return (
    isBattlecardUserFromDealsResponse(dealsResponse) &&
    isLegacyMoveInCashPlanFromDealsResponse(dealsResponse) &&
    !isLegacyCheckDeliveredFromDealsResponse(dealsResponse)
  );
};

/**
 * An instant-release member whose check request is not delivered yet. Instant release has no move-in plan,
 * so this follows the check request, not move_in_cash_plan.
 */
export const isInstantReleaseCheckPendingFromDealsResponse = (dealsResponse) => {
  const checkStatus = getLegacyCheckStatusValueFromDealsResponse(dealsResponse);
  return isInstantReleaseUserFromDealsResponse(dealsResponse) && checkStatus !== null && checkStatus !== "delivered";
};

/** Check statuses that mean the request was rejected and will never be processed. */
export const LEGACY_CHECK_ERROR_STATUSES = ["leaseoverride"];

export const isLegacyCheckErrorStatus = (status) =>
  LEGACY_CHECK_ERROR_STATUSES.includes(String(status ?? "").toLowerCase());

export const isLegacyCheckNotProcessedFromDealsResponse = (dealsResponse) =>
  isLegacyCheckErrorStatus(getLegacyCheckStatusValueFromDealsResponse(dealsResponse));

export const getLegacyCheckErrorFromDealsResponse = (dealsResponse) => {
  const checkStatus = getLegacyCheckStatusFromDealsResponse(dealsResponse);

  return {
    title: checkStatus?.error_title || "We couldn't process your check request",
    description:
      checkStatus?.error_description || "Please contact your locator to submit your rebate.",
    amount: getCheckStatusAmountFromDealsResponse(dealsResponse),
  };
};

export const getLegacyCheckRouteFromDealsResponse = (dealsResponse) => {
  if (isLegacyCheckNotProcessedFromDealsResponse(dealsResponse)) {
    return "/battlecard/check-not-processed";
  }

  if (getLegacyCheckStatusFromDealsResponse(dealsResponse)) {
    return "/battlecard/check-on-the-way";
  }

  // No check_status yet, so the request hasn't been submitted and the rebate terms still gate it.
  return "/battlecard/legacy-terms";
};

export const isBasicMoveInCashPlanFromDealsResponse = (dealsResponse) => {
  return (
    dealsResponse?.data?.message === "SUCCESS" &&
    dealsResponse?.data?.data?.currentDeal?.move_in_cash_plan === "basic"
  );
};

const DEFAULT_BASIC_SPENDING_QUALIFIER_FOR_REBATE = 250;

export const getBasicSpendingQualifierForRebateFromDealsResponse = (dealsResponse) => {
  const raw = dealsResponse?.data?.data?.currentDeal?.basic?.spending_qualifier_for_rebate;
  const amount = Number(raw);
  if (!Number.isFinite(amount)) {
    return DEFAULT_BASIC_SPENDING_QUALIFIER_FOR_REBATE;
  }
  return amount;
};

export const formatBasicSpendingQualifierForRebateFromDealsResponse = (dealsResponse) => {
  return formatCurrency(getBasicSpendingQualifierForRebateFromDealsResponse(dealsResponse), 0);
};

export const isUnlimitedMoveInCashPlanFromDealsResponse = (dealsResponse) => {
  return (
    dealsResponse?.data?.message === "SUCCESS" &&
    dealsResponse?.data?.data?.currentDeal?.move_in_cash_plan === "unlimited"
  );
};

export const isBasicOrUnlimitedMoveInCashPlanFromDealsResponse = (dealsResponse) => {
  return (
    isBasicMoveInCashPlanFromDealsResponse(dealsResponse) ||
    isUnlimitedMoveInCashPlanFromDealsResponse(dealsResponse)
  );
};

export const BATTLECARD_DEFAULT_PAGE_SUBTITLE =
  "Get more cash and faster! Choose the best option for you.";

export const BATTLECARD_VERIFICATION_REJECTED_LEGACY_SUBTITLE =
  "Since the verification was not approved, please proceed with a legacy request.";

export const BATTLECARD_VERIFICATION_REJECTED_LEGACY_TITLE =
  "Since the verification was not approved,\nplease proceed with a legacy request.";

export const getBattlecardPageSubtitle = (dealsResponse, stakeDebitResponse) => {
  if (
    isBasicOrUnlimitedMoveInCashPlanFromDealsResponse(dealsResponse) &&
    isSVDVerificationRejected(stakeDebitResponse?.data)
  ) {
    return BATTLECARD_VERIFICATION_REJECTED_LEGACY_SUBTITLE;
  }

  return BATTLECARD_DEFAULT_PAGE_SUBTITLE;
};

export const getMoveInCashPlanRoute = (dealsResponse) => {
  const plan = dealsResponse?.data?.data?.currentDeal?.move_in_cash_plan;

  if (plan == null) {
    return null;
  }

  switch (plan) {
    case "legacy": {
      if (isLegacyCheckDeliveredFromDealsResponse(dealsResponse)) return null;
      return getLegacyCheckRouteFromDealsResponse(dealsResponse);
    }
    case "basic":
      return "/battlecard/start-shopping";
    case "unlimited":
      return "/battlecard/pay-rent";
    default:
      return null;
  }
};

export const isSVDDenied = (stakeDebit) => {
  return !!stakeDebit && ["ApplicationDenied", "VerificationRejected"].includes(stakeDebit?.data?.status);
};

export const isSVDIneligible = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "Ineligible";
};

export const isSVDClosedForStakeSaverNonCompliance = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "ClosedForStakeSaverNonCompliance";
};

export const hasSVDPhysicalCardArrived = (arrivalDate) => {
  return new Date().getTime() >= arrivalDate?.getTime();
};

//Date time helpers

export function getThirdTuesdayOfMonth(y, m) {
  let date = new Date(y, m, 1);
  let dayOfWeek = date.getDay();
  let daysUntilFirstTuesday = (9 - dayOfWeek) % 7;
  date.setDate(1 + daysUntilFirstTuesday + 14); // 14 days after the first Tuesday
  return date;
}

export const getThirdTuesdayOfMonthCached = (() => {
  const cache = new Map();

  return (y, m) => {
    const key = `${y}-${m}`;
    if (cache.has(key)) {
      return cache.get(key);
    }

    let thirdTuesdayDate = getThirdTuesdayOfMonth(y, m);
    cache.set(key, thirdTuesdayDate);

    return thirdTuesdayDate;
  };
})();

export const isThirdTuesdayOfMonth = (d, m, y) => {
  const thirdTuesdayOfMonth = getThirdTuesdayOfMonthCached(y, m);
  return d === thirdTuesdayOfMonth.getDate();
};

export function getNextThirdTuesday() {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth();

  // Function to get the third Tuesday of a given month and year
  let thirdTuesday = getThirdTuesdayOfMonth(year, month);

  // If the third Tuesday has passed, get the third Tuesday of next month
  if (thirdTuesday < now) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
    thirdTuesday = getThirdTuesdayOfMonth(year, month);
  }

  return thirdTuesday;
}

//Misc helpers

export const onSignOut = ({ redirectOnLogout = true } = {}) => {
  clearBankMigrationSkippedStatus();
  clearBattlecardFlowAccess();

  LocalStorage.remove(appUtilityKeys.authKey);
  LocalStorage.remove(appUtilityKeys.loggedInUserData);
  LocalStorage.remove(appUtilityKeys.userSfid);
  LocalStorage.remove(appUtilityKeys.transferToken);
  LocalStorage.remove(appUtilityKeys.astraToken);
  LocalStorage.remove(appUtilityKeys.astraRefreshToken);
  LocalStorage.remove(appUtilityKeys.astraIntentId);
  LocalStorage.remove(appUtilityKeys.lastAstraTokenFetchTime);
  LocalStorage.remove(appUtilityKeys.astraSelectedCardId);
  LocalStorage.remove(appUtilityKeys.stakePay3AuthToken);

  if (!!window.uxc) {
    window.uxc.clearSession();
  }

  if (!!window.heap) {
    window.heap.resetIdentity();
  }

  if (CoralogixRum.isInited) {
    CoralogixRum.shutdown();
  }

  if (redirectOnLogout) {
    window.location.pathname = "/login";
  }
};

export const getHasVisitedWelcomeScreenKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return appUtilityKeys.hasVisitedWelcomeScreen(userId);
};

export const getBankMigrationSkippedStatusKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return userId ? appUtilityKeys.bankMigrationSkippedStatus(userId) : null;
};

export const getMoveInCashUnavailableModalSeenKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return userId ? appUtilityKeys.moveInCashUnavailableModalSeen(userId) : null;
};

export const hasSeenMoveInCashUnavailableModal = () => {
  const key = getMoveInCashUnavailableModalSeenKey();
  return !!key && !!LocalStorage.read(key);
};

export const markMoveInCashUnavailableModalSeen = () => {
  const key = getMoveInCashUnavailableModalSeenKey();
  if (key) {
    LocalStorage.write(key, true);
  }
};

export const shouldShowMoveInCashUnavailableModalFromDealsResponse = (dealsResponse) => {
  return (
    dealsResponse?.data?.message === "SUCCESS" &&
    dealsResponse?.data?.data?.currentDeal?.show_move_in_cash_unavailable_modal === true &&
    !hasSeenMoveInCashUnavailableModal()
  );
};

export const shouldShowMoveInCashUnavailableNoticeFromDealsResponse = (dealsResponse) => {
  return (
    dealsResponse?.data?.message === "SUCCESS" &&
    dealsResponse?.data?.data?.currentDeal?.show_move_in_cash_unavailable_modal === true
  );
};

export const clearBankMigrationSkippedStatus = () => {
  const bankMigrationSkippedStatusKey = getBankMigrationSkippedStatusKey();
  if (bankMigrationSkippedStatusKey) {
    LocalStorage.remove(bankMigrationSkippedStatusKey);
  }
};

export const getAnimateAvailableBalanceFromKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return appUtilityKeys.animateAvailableBalanceFrom(userId);
};

export const getUserSfid = () => LocalStorage.read(appUtilityKeys.userSfid);

export const getUserId = () => LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;

export const getUserEmail = () => LocalStorage.read(appUtilityKeys.loggedInUserData)?.email;

export const getTransferTypes = (dealsData) => {
  let instantDescription = `Arrives in minutes\n`;
  if (!(dealsData?.data?.currentDeal?.has_used_1_free_instant_transfer__c ?? true)) {
    instantDescription += `${
      dealsData?.data?.currentDeal?.has_used_1_free_instant_transfer__c ? 0 : 1
    } free use remaining`;
  }

  return [
    ...(dealsData?.data?.currentDeal?.enabled_for_instant_transfer__c
      ? [
          {
            type: "instant",
            Icon: Blitz,
            title: "Instant",
            description: instantDescription,
          },
        ]
      : []),
    {
      type: "ach",
      Icon: BankingOutline,
      title: "Standard",
      description: `Est. arrival:\n${dealsData?.content?.transaction_clearing_date}`,
    },
  ];
};

export const saveAndUpdateUserOnLogin = (data) => {
  LocalStorage.write(appUtilityKeys.authKey, data?.data?.[appUtilityKeys.authKey]);
  LocalStorage.write(appUtilityKeys.loggedInUserData, data?.data?.[appUtilityKeys.loggedInUserData]);

  if (!!window.uxc) {
    window.uxc.setUserIdentity(data?.data?.user?.phoneNumber ?? "");
    if (!data.data.user.phoneNumber) {
      window.uxc.setUserIdentity(data?.data?.user?.email ?? "");
    }

    window.uxc.setUserProperties({
      user_id: data?.data?.user?._id ?? "",
    });
  }

  if (!!window.heap) {
    window.heap.identify(data?.data?.user?.email ?? "");
  }

  if (CoralogixRum.isInited) {
    CoralogixRum.setUserContext({
      user_id: data?.data?.user?._id,
      user_email: data?.data?.user?.email,
      user_metadata: { phone_number: data?.data?.user?.phoneNumber },
    });
  }
};

export const isSelectedTransferMethodBank = (selectedMethod) => selectedMethod === "ach";
export const isSelectedTransferMethodInstant = (selectedMethod) => selectedMethod === "instant";

export const isSelectedAutomaticFundingMethodExpressPaycheck = (selectedAutomaticFundingMethod) =>
  selectedAutomaticFundingMethod === "express-paycheck";
export const isSelectedAutomaticFundingMethodDirectDeposit = (selectedAutomaticFundingMethod) =>
  selectedAutomaticFundingMethod === "direct-deposit";

export const getIsDebitCardExpired = (expiryMonthYear) => {
  var creditCardDate = moment(expiryMonthYear, "MM/YY");
  var today = moment();

  return !creditCardDate.isValid() || today >= creditCardDate.add(1, "months");
};

export const getPublicIPAddress = async () => {
  try {
    const response = await fetch("https://checkip.amazonaws.com/");
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const ip = await response.text();
    return ip.trim(); // Trim to remove any extra spaces or new lines
  } catch (error) {
    console.error("Error fetching the IP address:", error);
    return null;
  }
};

export const formatBirthDate = (birthDate) => {
  // If birthDate is empty, return an empty string
  if (!birthDate) {
    return "";
  }

  // Parse the input date string to a Date object
  const date = new Date(birthDate);

  // Check if the date is valid
  if (isNaN(date)) {
    return "1990-01-01"; // Fallback to default date
  }

  // Format the date to 'yyyy-MM-dd' (e.g., 2023-11-05)
  const outputDate = getISOFormatDate(date);
  return outputDate;
};

export const extract5DigitZipCode = (zipCode) => {
  if (!zipCode) {
    return null;
  }
  const match = zipCode.match(/^\d{5}/);
  return match ? match[0] : null;
};

export const getAstraCardsWithSystemCardFiltered = (astraCards, stakeDebit) => {
  const unitLast4 = stakeDebit?.data?.data?.card_last_4;
  const virtualUnitLast4 = stakeDebit?.data?.data?.virtual_last_4_digits;

  return (
    astraCards?.filter((card) => card.last_four_digits !== unitLast4 && card.last_four_digits !== virtualUnitLast4) ??
    []
  );
};

export const getSelectedAstraCard = (astraCards, stakeDebit, preferredCardId = null) => {
  const filteredOutCards = getAstraCardsWithSystemCardFiltered(astraCards, stakeDebit);

  if (filteredOutCards.length > 0) {
    const selectedId = !!preferredCardId ? preferredCardId : null;
    const foundSelectedCard = filteredOutCards.filter((card) => card.id === selectedId);
    if (!!selectedId && foundSelectedCard.length > 0) {
      return foundSelectedCard[0];
    } else {
      return filteredOutCards[0];
    }
  } else {
    return null;
  }
};

export const getActiveSVDAstraDebitCard = (astraCards, stakeDebit) => {
  const unitLast4 = stakeDebit?.data?.data?.card_last_4 ?? "";
  const virtualUnitLast4 = stakeDebit?.data?.data?.virtual_last_4_digits ?? "";

  const unitCard = astraCards?.find((card) => card.last_four_digits === unitLast4);
  if (unitCard) {
    return unitCard;
  }

  const virtualCard = astraCards?.find((card) => card.last_four_digits === virtualUnitLast4);
  if (virtualCard) {
    return virtualCard;
  }

  return null;
};

export const getRecurringDepositDefaultPreferences = () => {
  return { startDate: getISOFormatDate(moment().add(1, "days")), frequency: "Weekly" };
};

export const getNumberOrdinal = (num) => {
  const ords = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return ords[(v - 20) % 10] || ords[v] || ords[0];
};

export const getNumberWithOrdinal = (num) => {
  return `${num}${getNumberOrdinal(num)}`;
};

export const convertObjectToFormData = (obj) => {
  const formData = new FormData();
  Object.entries(obj).forEach(([k, v]) => formData.append(k, v));
  return formData;
};

export const zPad = (n, width, z) => {
  z = z || "0";
  n = n + "";
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
};

export function getAcronym(text) {
  if (!text) return "";
  return text
    .trim()
    .split(/\s+/)
    .map((word) => word[0] || "")
    .join("")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
}

export function getRecurringStartDate(frequency, recurringDay, fromDate = normalizeDateToMidnight()) {
  const date = new Date(fromDate);

  if (!!recurringDay) {
    if (frequency === "weekly" || frequency === "biweekly") {
      const targetDay = weekdayNames.indexOf(capitaliseString(recurringDay)) + 1;
      let diff = targetDay - date.getDay();
      if (diff <= 0) diff += 7;
      date.setDate(date.getDate() + diff);
      return date;
    }

    if (frequency === "monthly") {
      const day = Number(recurringDay);
      const next = new Date(date.getFullYear(), date.getMonth(), day);
      if (next <= date) {
        return new Date(date.getFullYear(), date.getMonth() + 1, day);
      }
      return next;
    }
  }

  return null;
}

export function getRecurringEndDate({ frequency, startDate, durationType, noOfPayments, specificDate }) {
  if (durationType === "until i cancel") {
    return null;
  }
  if (durationType === "until specific date") {
    const endLimit = normalizeDateToMidnight(specificDate);
    let date = normalizeDateToMidnight(startDate);
    if (date > endLimit) return null;
    if (frequency === "weekly" || frequency === "biweekly") {
      const interval = frequency === "weekly" ? 7 : 14;
      while (true) {
        const next = new Date(date);
        next.setDate(next.getDate() + interval);
        if (next > endLimit) break;
        date = next;
      }
      return date;
    } else if (frequency === "monthly") {
      const startDate = date.getDate();
      let month = date.getMonth();
      let year = date.getFullYear();
      while (true) {
        const next = new Date(year, month + 1, startDate);
        if (next > endLimit) break;
        date = next;
        month++;
      }
      return date;
    }
  }
  if (durationType === "until specific number of payments") {
    if (!noOfPayments) return null;
    const date = normalizeDateToMidnight(startDate);
    for (let i = 1; i < noOfPayments; i++) {
      if (frequency === "weekly") {
        date.setDate(date.getDate() + 7);
      } else if (frequency === "biweekly") {
        date.setDate(date.getDate() + 14);
      } else if (frequency === "monthly") {
        date.setMonth(date.getMonth() + 1);
      }
    }
    return date;
  }

  return null;
}

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};
